import { Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export interface InlineUrlOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    inlineUrl: {
      /**
       * Insert an inline URL
       */
      insertUrl: (href: string) => ReturnType;
    };
  }
}

/**
 * TipTap extension for inline URLs
 * Supports \url{...} syntax for clickable links
 */
export const InlineUrl = Node.create<InlineUrlOptions>({
  name: "inlineUrl",
  group: "inline",
  inline: true,
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      href: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-href") || "",
        renderHTML: (attributes) => ({
          "data-href": attributes.href,
        }),
      },
      // The link's text, for \href{url}{text}; empty for a bare \url{url},
      // which shows the address itself.
      text: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-text") || "",
        renderHTML: (attributes) => (attributes.text ? { "data-text": attributes.text } : {}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-type="inline-url"]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const href = node.attrs.href as string;
    const text = node.attrs.text as string;

    // Truncate display for very long URLs
    let displayText = text || href;
    if (!text && displayText.length > 50) {
      displayText = displayText.slice(0, 47) + "...";
    }

    return [
      "a",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "inline-url",
        "data-href": href,
        href,
        target: "_blank",
        rel: "noopener noreferrer",
        class: "inline-url",
        title: href,
      }),
      displayText,
    ];
  },

  addCommands() {
    return {
      insertUrl:
        (href: string) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { href },
          });
        },
    };
  },

  addProseMirrorPlugins() {
    const urlRegex = /\\url\{([^}]+)\}/g;

    return [
      new Plugin({
        key: new PluginKey("inlineUrlInputRule"),
        props: {
          decorations: (state) => {
            const decorations: Decoration[] = [];
            const doc = state.doc;

            doc.descendants((node, pos) => {
              if (node.isText && node.text) {
                const text = node.text;
                let match: RegExpExecArray | null;

                urlRegex.lastIndex = 0;
                while ((match = urlRegex.exec(text)) !== null) {
                  const start = pos + match.index;
                  const end = start + match[0].length;

                  decorations.push(
                    Decoration.inline(start, end, {
                      class: "url-highlight",
                    })
                  );
                }
              }
            });

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});

export default InlineUrl;
